/**
 * oh-charte.jsx — Composants normés CHARTE-GRAPHIQUE-OMNIHUB (§4 Composants).
 *
 * 100 % dérivés des tokens `var(--oh-*)` du socle (frontend/src/index.css),
 * donc DUAL-MODE par construction : chaque composant est lisible et cohérent
 * en mode clair ET foncé, sans une seule couleur/taille/espacement en dur.
 *
 * Périmètre = les 4 composants manquants identifiés par l'audit charte :
 *  - <OhCard>            carte : fond --oh-bg-elevated, bord 0.5px --oh-border,
 *                        radius 12px (--oh-radius-card), padding 16px (--oh-space-4).
 *  - <OhStatusNote>      encart d'état / message : variant success|error|info|neutral,
 *                        paires --oh-{success|error|info}-{bg,text,border}, radius 8px.
 *  - <OhField>+<OhInput> input/select : hauteur 36px, radius 8px (--oh-radius-control),
 *    +<OhSelect>         bord 0.5px, focus = anneau accent (--oh-accent).
 *  - <OhSideMenu>        menu latéral : entrée active = --oh-accent-bg + --oh-accent-text.
 *    +<OhMenuItem>
 *    +<OhMenuSection>
 *
 * Réf. tokens : --oh-bg(-elevated/-surface/-hover), --oh-text(-secondary/-muted),
 * --oh-border, --oh-accent(-bg/-text/-solid/-contrast), --oh-{success|error|info}-*,
 * --oh-space-{1..8}, --oh-radius-{control|card}.
 *
 * NB : on ne remplace PAS les primitives shadcn (ui/button, ui/card…) massivement
 * utilisées ; ces composants sont le gabarit charte à généraliser progressivement,
 * comme le DAL (cf. CLAUDE.md § Frontend theming).
 */

import React, { useState, forwardRef } from "react";
import { CheckCircle2, AlertCircle, Info, AlertTriangle } from "lucide-react";

const cx = (...c) => c.filter(Boolean).join(" ");

/* ============================================================================
 * 1. OhCard — carte normée
 * ==========================================================================*/

/**
 * Carte : surface élevée, bord fin, radius 12px, padding 16px (charte §4).
 * Props : padded (bool, défaut true) · as (élément, défaut "div") · style/className/…
 */
export const OhCard = forwardRef(function OhCard(
  { as: As = "div", padded = true, className = "", style = {}, children, ...rest },
  ref
) {
  return (
    <As
      ref={ref}
      className={className}
      style={{
        background: "var(--oh-bg-elevated)",
        border: "0.5px solid var(--oh-border)",
        borderRadius: "var(--oh-radius-card)",
        padding: padded ? "var(--oh-space-4)" : 0,
        color: "var(--oh-text)",
        ...style,
      }}
      {...rest}
    >
      {children}
    </As>
  );
});

/** En-tête de carte (titre 500 + description discrète). */
export function OhCardHeader({ title, description, right, className = "", style = {} }) {
  return (
    <div
      className={cx("flex items-start justify-between gap-3", className)}
      style={{ marginBottom: "var(--oh-space-3)", ...style }}
    >
      <div className="min-w-0">
        {title && (
          <h3 className="truncate" style={{ fontWeight: 500, fontSize: 16, color: "var(--oh-text)", lineHeight: 1.4 }}>
            {title}
          </h3>
        )}
        {description && (
          <p style={{ fontSize: 14, color: "var(--oh-text-secondary)", lineHeight: 1.6, marginTop: 2 }}>
            {description}
          </p>
        )}
      </div>
      {right != null && <div className="shrink-0">{right}</div>}
    </div>
  );
}

/* ============================================================================
 * 2. OhStatusNote — encart d'état / message (succès / erreur / info / neutre)
 * ==========================================================================*/

const STATUS = {
  success: { bg: "var(--oh-success-bg)", text: "var(--oh-success-text)", border: "var(--oh-success-border)", Icon: CheckCircle2 },
  error: { bg: "var(--oh-error-bg)", text: "var(--oh-error-text)", border: "var(--oh-error-border)", Icon: AlertCircle },
  info: { bg: "var(--oh-info-bg)", text: "var(--oh-info-text)", border: "var(--oh-info-border)", Icon: Info },
  // "warning" = même famille sémantique que info dans la charte (ambre) ; alias explicite.
  warning: { bg: "var(--oh-info-bg)", text: "var(--oh-info-text)", border: "var(--oh-info-border)", Icon: AlertTriangle },
  // neutre = surface + bord socle (aucune sémantique de couleur).
  neutral: { bg: "var(--oh-bg-surface)", text: "var(--oh-text-secondary)", border: "var(--oh-border)", Icon: Info },
};

/**
 * Encart d'état charte : fond teinté + texte foncé même famille + bord, radius 8px.
 * Contraste garanti dans les 2 modes (paires dual-mode). Sert aussi de « message ».
 * Props : variant "success|error|info|warning|neutral" · title? · icon (bool, défaut true)
 *         · children (corps) · className/style/…
 */
export function OhStatusNote({
  variant = "info",
  title,
  icon = true,
  className = "",
  style = {},
  children,
  ...rest
}) {
  const s = STATUS[variant] || STATUS.info;
  const Icon = s.Icon;
  return (
    <div
      role="status"
      className={cx("flex items-start gap-2", className)}
      style={{
        background: s.bg,
        color: s.text,
        border: `1px solid ${s.border}`,
        borderRadius: "var(--oh-radius-control)",
        padding: "var(--oh-space-3)",
        fontSize: 14,
        lineHeight: 1.6,
        ...style,
      }}
      {...rest}
    >
      {icon && <Icon className="w-4 h-4 shrink-0" style={{ marginTop: 2 }} />}
      <div className="min-w-0">
        {title && <div style={{ fontWeight: 500, marginBottom: children ? 2 : 0 }}>{title}</div>}
        {children}
      </div>
    </div>
  );
}

/* ============================================================================
 * 3. OhInput / OhSelect / OhField — contrôles de saisie normés
 * ==========================================================================*/

// Style commun contrôle : 36px, radius 8px, bord 0.5px, surface élevée.
const controlBase = {
  height: 36,
  width: "100%",
  padding: "0 var(--oh-space-3)",
  borderRadius: "var(--oh-radius-control)",
  border: "0.5px solid var(--oh-border)",
  background: "var(--oh-bg-elevated)",
  color: "var(--oh-text)",
  fontSize: 14,
  outline: "none",
  transition: "box-shadow .12s ease, border-color .12s ease",
};

// Anneau de focus accent (accessibilité charte §6) — appliqué en JS pour rester
// self-contained (pas de dépendance à une classe utilitaire globale).
const focusRing = (focused, invalid) =>
  invalid
    ? { borderColor: "var(--oh-error-border)", boxShadow: "0 0 0 3px var(--oh-error-bg)" }
    : focused
    ? { borderColor: "var(--oh-accent)", boxShadow: "0 0 0 3px var(--oh-accent-bg)" }
    : {};

/** Input normé (36px, focus anneau accent). */
export const OhInput = forwardRef(function OhInput(
  { invalid = false, className = "", style = {}, onFocus, onBlur, ...rest },
  ref
) {
  const [f, setF] = useState(false);
  return (
    <input
      ref={ref}
      className={className}
      style={{ ...controlBase, ...focusRing(f, invalid), ...style }}
      onFocus={(e) => { setF(true); onFocus?.(e); }}
      onBlur={(e) => { setF(false); onBlur?.(e); }}
      {...rest}
    />
  );
});

/** Select normé (même gabarit que l'input). */
export const OhSelect = forwardRef(function OhSelect(
  { invalid = false, className = "", style = {}, onFocus, onBlur, children, ...rest },
  ref
) {
  const [f, setF] = useState(false);
  return (
    <select
      ref={ref}
      className={className}
      style={{ ...controlBase, cursor: "pointer", ...focusRing(f, invalid), ...style }}
      onFocus={(e) => { setF(true); onFocus?.(e); }}
      onBlur={(e) => { setF(false); onBlur?.(e); }}
      {...rest}
    >
      {children}
    </select>
  );
});

/**
 * Champ complet : label (500) + contrôle + hint/erreur. Enveloppe OhInput/OhSelect
 * ou tout enfant custom. Props : label · htmlFor · hint · error (string) · required.
 */
export function OhField({ label, htmlFor, hint, error, required, className = "", style = {}, children }) {
  return (
    <div className={className} style={{ display: "flex", flexDirection: "column", gap: "var(--oh-space-1)", ...style }}>
      {label && (
        <label htmlFor={htmlFor} style={{ fontSize: 14, fontWeight: 500, color: "var(--oh-text)" }}>
          {label}
          {required && <span style={{ color: "var(--oh-error-text)" }}> *</span>}
        </label>
      )}
      {children}
      {error ? (
        <span style={{ fontSize: 12, color: "var(--oh-error-text)" }}>{error}</span>
      ) : hint ? (
        <span style={{ fontSize: 12, color: "var(--oh-text-muted)" }}>{hint}</span>
      ) : null}
    </div>
  );
}

/* ============================================================================
 * 4. OhSideMenu / OhMenuSection / OhMenuItem — menu latéral normé
 * ==========================================================================*/

/** Conteneur de menu latéral. */
export function OhSideMenu({ className = "", style = {}, children, ...rest }) {
  return (
    <nav
      className={className}
      style={{ display: "flex", flexDirection: "column", gap: "var(--oh-space-1)", ...style }}
      {...rest}
    >
      {children}
    </nav>
  );
}

/** Titre de section de menu (label discret, sentence case). */
export function OhMenuSection({ label, className = "", style = {} }) {
  return (
    <div
      className={className}
      style={{
        fontSize: 12,
        fontWeight: 500,
        color: "var(--oh-text-muted)",
        padding: "var(--oh-space-2) var(--oh-space-3) var(--oh-space-1)",
        ...style,
      }}
    >
      {label}
    </div>
  );
}

/**
 * Entrée de menu latéral. Active = fond --oh-accent-bg + texte --oh-accent-text
 * (charte §4). Inactive = texte secondaire ; hover = fond --oh-bg-hover.
 * Props : active (bool) · icon (élément) · as (défaut "button") · onClick · …
 */
export const OhMenuItem = forwardRef(function OhMenuItem(
  { active = false, icon = null, as: As = "button", className = "", style = {}, children, ...rest },
  ref
) {
  const [hover, setHover] = useState(false);
  return (
    <As
      ref={ref}
      type={As === "button" ? "button" : undefined}
      aria-current={active ? "true" : undefined}
      className={cx("w-full flex items-center gap-2.5 text-left", className)}
      style={{
        padding: "var(--oh-space-2) var(--oh-space-3)",
        borderRadius: "var(--oh-radius-control)",
        fontSize: 14,
        fontWeight: 500,
        border: "none",
        cursor: "pointer",
        transition: "background .12s ease, color .12s ease",
        background: active ? "var(--oh-accent-bg)" : hover ? "var(--oh-bg-hover)" : "transparent",
        color: active ? "var(--oh-accent-text)" : "var(--oh-text-secondary)",
        ...style,
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      {...rest}
    >
      {icon && <span className="shrink-0" style={{ display: "inline-flex" }}>{icon}</span>}
      <span className="truncate">{children}</span>
    </As>
  );
});

export default {
  OhCard, OhCardHeader,
  OhStatusNote,
  OhInput, OhSelect, OhField,
  OhSideMenu, OhMenuSection, OhMenuItem,
};
